import { useMemo, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { DndContext, DragOverlay } from '@dnd-kit/core'
import type { DraftState, WizardItemType } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import { selectBoardItems } from '../../../Flux/Selector/OrderWizard/OrderWizardSelectors.ts'
import { useStableCallback } from '../../../Framework/View/Hooks/useLatest.ts'
import type { PoolTarget } from '../../../Services/OrderWizard/pools.ts'
import { contentsOf, newPoolNumber, poolList, withPoolNames } from '../../../Services/OrderWizard/pools.ts'
import type { Board } from '../../../Services/OrderWizard/poolBoard.ts'
import { boardOf, editAfterMove, markAction, overlayEdit, sharedPrefix } from '../../../Services/OrderWizard/poolBoard.ts'
import { poolNamesOf } from '../../../Services/OrderWizard/sheet/sheetRoles.ts'
import { plural } from '../../../Util/plural.ts'
import { InfoDisclosure } from './InfoDisclosure.tsx'
import { NEW_POOL } from './Primitives.tsx'
import type { Attention } from './PoolRows.tsx'
import { ItemList, NewPoolColumn, PoolColumn } from './PoolColumns.tsx'
import { SelectionBar } from './SelectionBar.tsx'
import { PendingPoolsBar } from './PendingPoolsBar.tsx'
import { usePoolSelection } from './usePoolSelection.ts'
import { DRAG_MODIFIERS, usePoolDrag } from './usePoolDrag.ts'
import { usePendingPoolEdit } from './PendingPoolEdit.tsx'

const NO_KEYS: ReadonlySet<string> = new Set()

/**
 * One list while the order is one pool, else the board, a column per pool. Type marks apply at once;
 * moves wait in the pending pool edit for "Use these pools", so a half-done split never reaches the order.
 */
export const PoolAssignment = ({
  draft,
  flagged,
  layout
}: {
  draft: DraftState
  /** Items an issue of the step names. */
  flagged: ReadonlySet<string>
  /** `'list'` shows one pool's items without columns, selection, "New pool", "Move to" or dragging. */
  layout: 'list' | 'board'
}) => {
  const dispatch = useDispatch()
  const { edit, setEdit: onEdit, apply, discard } = usePendingPoolEdit()
  const { order, names, titles, tags, controls, autoControls, errors, pools: committed } = selectBoardItems(draft)
  const prefix = useMemo(() => sharedPrefix(Object.values(names)), [names])
  const poolNames = useMemo(() => poolNamesOf(draft, committed), [draft, committed])
  const attention = useMemo(() => {
    const marks: Record<string, Attention> = {}
    for (const key of order) {
      if (errors.has(key)) {
        marks[key] = 'error'
      } else if (flagged.has(key)) {
        marks[key] = 'flagged'
      }
    }
    return marks
  }, [order, errors, flagged])
  const board = useMemo<Board>(
    () => (edit ? { pools: overlayEdit(committed, edit, order), columns: edit.columns } : boardOf(committed)),
    [edit, order, committed]
  )
  const [moveTarget, setMoveTarget] = useState('')
  const list = layout === 'list'

  const columnContents = useMemo(() => contentsOf(order, (key) => controls.has(key), board.pools), [order, controls, board.pools])
  const contents = useMemo(() => new Map(columnContents.map((pool) => [pool.pool, pool])), [columnContents])
  const pools = useMemo(() => [...new Set([...board.columns, ...contents.keys()])].sort((a, b) => a - b), [board.columns, contents])
  /* the sheet's names follow the pools as the draft has them, not an unapplied edit */
  const columns = useMemo(
    () => withPoolNames(poolList(pools.map((pool) => [pool, contents.get(pool)?.keys.length ?? 0])), edit ? {} : poolNames),
    [pools, contents, edit, poolNames]
  )
  const filled = pools.filter((pool) => contents.has(pool)).length
  const columnPrefix = pools.length > 1 ? prefix : ''
  const {
    selection,
    byColumn,
    onClickItem,
    onCheck,
    clear: clearSelection
  } = usePoolSelection(
    columnContents,
    (key) => contents.get(board.pools[key] ?? 0)?.keys ?? []
  )

  const onSetType = useStableCallback((key: string, itemType: WizardItemType) =>
    dispatch(wizard.itemTypeToggled({ draftId: draft.id, groupKeys: [key], itemType }))
  )
  const onRemoveItem = useStableCallback((key: string) => dispatch(wizard.itemsRemoved({ draftId: draft.id, groupKeys: [key] })))
  const clear = clearSelection

  const move = (keys: string[], target: PoolTarget) => onEdit(editAfterMove(board, keys, target, order, committed))

  const moveSelection = () => {
    if (!moveTarget) {
      return
    }
    move([...selection.keys], moveTarget === NEW_POOL ? 'new' : Number(moveTarget))
    setMoveTarget('')
    clear()
  }

  const marked = [...selection.keys].filter((key) => key in names)
  const marking = markAction(marked, controls)
  const mark = () => dispatch(wizard.itemTypeToggled({ draftId: draft.id, groupKeys: marking.groupKeys, itemType: marking.itemType }))

  const remove = () => {
    if (marked.length > 0) {
      dispatch(wizard.itemsRemoved({ draftId: draft.id, groupKeys: marked }))
    }
    clear()
  }

  const { dragged, draggedSet, onPress, context } = usePoolDrag({ selection, onMove: move, onDropSelection: clear })
  const count = (n: number) => plural(n, 'item')

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && dragged.length === 0 && selection.keys.size > 0) {
      clear()
    }
  }

  const selectedCount = selection.keys.size
  const onlyPool = pools[0] ?? 0
  return (
    <div
      className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 text-sm"
      data-testid="wizard-pool-assignment"
      data-layout={layout}
      onKeyDown={onKeyDown}
    >
      {list ? (
        <>
          <InfoDisclosure>
            <p>
              Switch an item between sample and negative control on its row; the bin removes it from the order. Tick items to mark or remove
              several at once. Changes apply at once; a removed item can be re-added below the list.
            </p>
          </InfoDisclosure>
          {selectedCount > 0 && (
            <SelectionBar
              selected={selectedCount}
              markLabel={marking.label}
              onMark={mark}
              onRemove={remove}
              onClear={clear}
            />
          )}
          <ItemList
            pool={onlyPool}
            contents={contents.get(onlyPool)}
            names={names}
            titles={titles}
            tags={tags}
            controls={controls}
            autoControls={autoControls}
            attention={attention}
            selected={byColumn.get(onlyPool) ?? NO_KEYS}
            onSetType={onSetType}
            onCheck={onCheck}
            onRemove={onRemoveItem}
          />
        </>
      ) : (
        <>
          <InfoDisclosure>
            <p>
              Drag items between pools, or select them and use the bar above the pools. Switch an item between sample and negative control
              on its row. Click selects an item, shift-click a range in its pool, Ctrl- or ⌘-click adds or removes one; Esc clears.
              Negative-control marks apply at once, pool changes when you use them.
            </p>
          </InfoDisclosure>
          <span className="sr-only" aria-live="polite">
            {selectedCount > 0 ? `${count(selectedCount)} selected` : ''}
          </span>
          {/* keeps the bar's height while nothing is selected, so the board does not jump */}
          <div className="h-9" data-testid="wizard-board-selection-slot">
            {selectedCount > 0 && (
              <SelectionBar
                selected={selectedCount}
                pools={columns}
                moveTarget={moveTarget}
                onMoveTarget={setMoveTarget}
                onMove={moveSelection}
                markLabel={marking.label}
                onMark={mark}
                onRemove={remove}
                onClear={clear}
              />
            )}
          </div>
          <DndContext {...context}>
            {columnPrefix && (
              <p className="flex min-w-0 items-baseline gap-1 text-xs text-slate-600" data-testid="wizard-board-prefix">
                <span className="shrink-0">Names start with</span>
                <code className="min-w-0 truncate rounded bg-slate-100 px-1 font-mono text-slate-800" title={columnPrefix}>
                  {columnPrefix}…
                </code>
              </p>
            )}
            <div className="flex max-h-[50vh] min-h-40 gap-2 overflow-x-auto pb-1" data-testid="wizard-pool-board">
              {columns.map(({ pool, number, name }) => (
                <PoolColumn
                  key={pool}
                  pool={pool}
                  number={number}
                  several={filled > 1}
                  wide={pools.length === 1}
                  contents={contents.get(pool)}
                  committed={!edit}
                  name={name}
                  names={names}
                  titles={titles}
                  tags={tags}
                  prefix={columnPrefix}
                  controls={controls}
                  autoControls={autoControls}
                  attention={attention}
                  selected={byColumn.get(pool) ?? NO_KEYS}
                  dragged={draggedSet}
                  dragging={dragged.length > 0}
                  onPress={onPress}
                  onClickItem={onClickItem}
                  onCheck={onCheck}
                  onSetType={onSetType}
                />
              ))}
              <NewPoolColumn number={newPoolNumber(columns)} dragging={dragged.length > 0} />
            </div>
            {createPortal(
              <DragOverlay dropAnimation={null} modifiers={DRAG_MODIFIERS}>
                {dragged.length > 0 && (
                  <div
                    className="pointer-events-none w-max rounded-full bg-slate-800 px-3 py-1 text-xs font-semibold text-white shadow-lg"
                    data-testid="wizard-board-drag-preview"
                  >
                    {dragged.length === 1 ? names[dragged[0]] : count(dragged.length)}
                  </div>
                )}
              </DragOverlay>,
              document.body
            )}
          </DndContext>
        </>
      )}
      {edit && <PendingPoolsBar onApply={apply} onDiscard={discard} />}
    </div>
  )
}
